import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { Link } from 'react-router-dom'
import * as yup from 'yup'
import _ from '@lodash'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import { useEffect } from 'react'
import jwtService from '../../auth/services/jwtService'

/**
 * Form Validation Schema
 */
const schema = yup.object().shape({
  email: yup
    .string()
    .email('Você deve inserir um e-mail válido')
    .required('Você deve inserir um e-mail'),
  password: yup
    .string()
    .required('Por favor, insira sua senha.')
    .min(4, 'A senha é muito curta - deve ter pelo menos 4 caracteres.')
})

const defaultValues = {
  email: '',
  password: '',
  remember: true
}

function SignInPage () {
  const { control, formState, handleSubmit, setError, setValue } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema)
  })

  const { isValid, dirtyFields, errors } = formState

  function onSubmit ({ email, password }) {
    jwtService
      .signInWithEmailAndPassword(email, password)
      .then(user => {
        // No need to do anything, user data will be set at app/auth/AuthContext
      })
      .catch(_errors => {
        _errors.forEach(error => {
          setError(error.type, {
            type: 'manual',
            message: error.message
          })
        })
      })
  }

  return (
    <div className='flex flex-col sm:flex-row items-center md:items-start sm:justify-center md:justify-start flex-1 min-w-0'>
      <Paper className='h-full sm:h-auto md:flex md:items-center md:justify-end w-full sm:w-auto md:h-full md:w-1/2 py-8 px-16 sm:p-48 md:p-64 sm:rounded-2xl md:rounded-none sm:shadow md:shadow-none ltr:border-r-1 rtl:border-l-1'>
        <div className='w-full max-w-320 sm:w-320 mx-auto sm:mx-0'>
          <img className='w-48' src='assets/images/logo/2201570.png' alt='logo' />

          <Typography className='mt-32 text-4xl font-extrabold tracking-tight leading-tight'>
            Entrar
          </Typography>
          <div className='flex items-baseline mt-2 font-medium'>
            <Typography>Não tem uma conta?</Typography>
            <Link className='ml-4' to='/sign-up'>
              Inscrever-se
            </Link>
          </div>

          <form
            name='loginForm'
            noValidate
            className='flex flex-col justify-center w-full mt-32'
            onSubmit={handleSubmit(onSubmit)}
          >
            <Controller
              name='email'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  className='mb-24'
                  label='Email'
                  autoFocus
                  type='email'
                  error={!!errors.email}
                  helperText={errors?.email?.message}
                  variant='outlined'
                  required
                  fullWidth
                />
              )}
            />

            <Controller
              name='password'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  className='mb-24'
                  label='Senha'
                  type='password'
                  error={!!errors.password}
                  helperText={errors?.password?.message}
                  variant='outlined'
                  required
                  fullWidth
                />
              )}
            />

            <div className='flex flex-col sm:flex-row items-center justify-center sm:justify-between'>
              <Controller
                name='remember'
                control={control}
                render={({ field }) => (
                  <FormControl>
                    <FormControlLabel
                      label='Lembrar-me'
                      control={<Checkbox size='small' {...field} />}
                    />
                  </FormControl>
                )}
              />

              <Link
                className='text-md font-medium'
                to='/pages/auth/forgot-password'
              >
                Esqueceu a senha?
              </Link>
            </div>

            <Button
              variant='contained'
              color='secondary'
              className=' w-full mt-16'
              aria-label='Sign in'
              disabled={_.isEmpty(dirtyFields) || !isValid}
              type='submit'
              size='large'
            >
              Entrar
            </Button>
          </form>
        </div>
      </Paper>

      <Box
        className='relative hidden md:flex flex-auto items-center justify-center h-full p-64 lg:px-112 overflow-hidden'
        sx={{ backgroundColor: 'primary.main' }}
      >
        <svg
          className='absolute inset-0 pointer-events-none'
          viewBox='0 0 960 540'
          width='100%'
          height='100%'
          preserveAspectRatio='xMidYMax slice'
          xmlns='http://www.w3.org/2000/svg'
        >
          <Box
            component='g'
            sx={{ color: 'primary.light' }}
            className='opacity-20'
            fill='none'
            stroke='currentColor'
            strokeWidth='100'
          >
            <circle r='234' cx='196' cy='23' />
            <circle r='234' cx='790' cy='491' />
          </Box>
        </svg>
        <Box
          component='svg'
          className='absolute -top-64 -right-64 opacity-20'
          sx={{ color: 'primary.light' }}
          viewBox='0 0 220 192'
          width='220px'
          height='192px'
          fill='none'
        >
          <defs>
            <pattern
              id='837c3e70-6c3a-44e6-8854-cc48c737b659'
              x='0'
              y='0'
              width='20'
              height='20'
              patternUnits='userSpaceOnUse'
            >
              <rect x='0' y='0' width='4' height='4' fill='currentColor' />
            </pattern>
          </defs>
          <rect
            width='220'
            height='192'
            fill='url(#837c3e70-6c3a-44e6-8854-cc48c737b659)'
          />
        </Box>

        <div className='z-10 relative w-full max-w-2xl'>
          <div className='text-7xl font-bold leading-none text-gray-100'>
            <div>Bem-vindo a</div>
            <div>nossa comunidade</div>
          </div>
          <div className='mt-24 text-lg tracking-tight leading-6 text-gray-400'>
            Aqui você tem acesso a vários cursos de graça, pagando apenas R$
            30,00 pela emissão do certificado.
          </div>
        </div>
      </Box>
    </div>
  )
}

export default SignInPage
